import {
  Box,
  Collapse,
  IconButton,
  LinearProgress,
  Slide,
  SxProps,
  Theme,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Icon } from '../../icon/icon';
import { AppMenuDrawer } from '../app-menu-drawer';
import { trackMenuClick } from '../../../helpers/analytics';

export type HeaderVariantName =
  | 'white'
  | 'sand'
  | 'saffron'
  | 'pistachio'
  | 'sky';

type HeaderImageProps = SxProps<Theme>;

type HeaderVariantProps = {
  bgColor: string;
  logoSrc: string;
  bgImageProps: HeaderImageProps | null;
};

type PropTypes = {
  variant: HeaderVariantName;
  onBack?: () => void;
  showProgressBar?: boolean;
  stepProgress?: number;
  showMenu?: boolean;
  showBgImage?: boolean;
};

const sharedProps: HeaderImageProps = {
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'clamp(200px, 100%, calc(100% - 72px))',
};

const BG_SAFFRON: HeaderImageProps = {
  ...sharedProps,
  backgroundImage: 'url("/header-bg-saffron.svg")',
};

const BG_PISTACHIO: HeaderImageProps = {
  ...sharedProps,
  backgroundImage: 'url("/header-bg-pistachio.svg")',
};

const BG_SKY: HeaderImageProps = {
  ...sharedProps,
  backgroundImage: 'url("/header-bg-sky.svg")',
};

const HEADER_VARIANTS: Record<HeaderVariantName, HeaderVariantProps> = {
  white: {
    bgColor: 'white',
    logoSrc: '/logo.svg',
    bgImageProps: null,
  },
  sand: {
    bgColor: 'background.default',
    logoSrc: '/logo.svg',
    bgImageProps: null,
  },
  saffron: {
    bgColor: 'secondary.main',
    logoSrc: '/logo-white.svg',
    bgImageProps: BG_SAFFRON,
  },
  pistachio: {
    bgColor: 'pistachio.main',
    logoSrc: '/logo-white.svg',
    bgImageProps: BG_PISTACHIO,
  },
  sky: {
    bgColor: 'sky.main',
    logoSrc: '/logo-white.svg',
    bgImageProps: BG_SKY,
  },
};

export const AppHeader = ({
  variant = 'saffron',
  onBack = null,
  showProgressBar,
  stepProgress = 0,
  showMenu = true,
  showBgImage = true,
}: PropTypes) => {
  const containerRef = useRef(null);
  const [isMenuOpen, setMenuOpen] = useState(false);

  const { bgColor, logoSrc, bgImageProps } =
    HEADER_VARIANTS[variant] || HEADER_VARIANTS.saffron;

  const toggleMenu = () => {
    trackMenuClick(!isMenuOpen);
    setMenuOpen(!isMenuOpen);
  };

  return (
    <Box
      ref={containerRef}
      bgcolor={bgColor}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        ...(showBgImage ? bgImageProps : null),
      }}
      py={1.5}
    >
      <Box
        sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Collapse in={!!onBack} unmountOnExit orientation="horizontal">
            <IconButton
              aria-label="Back"
              onClick={onBack}
              disabled={!onBack}
              color="inherit"
              sx={{ ml: 1 }}
            >
              <Icon icon="ArrowLeft" fontSize="small" />
            </IconButton>
          </Collapse>
          <Box
            component="img"
            src={logoSrc}
            alt="Talkiatry"
            sx={{
              height: '32px',
              userSelect: 'none',
              pointerEvents: 'none',
              zIndex: 1,
              ml: 2,
            }}
          />
        </Box>

        <Collapse in={!!showMenu} unmountOnExit orientation="horizontal">
          <Box sx={{ mr: 3 }}>
            <IconButton aria-label="Menu" color="inherit" onClick={toggleMenu}>
              <Icon
                icon="Bars"
                fontSize="small"
                sx={{ color: 'rgba(0,0,0,0.6)' }}
              />
            </IconButton>
          </Box>
        </Collapse>
      </Box>
      <Slide
        in={showProgressBar}
        direction="up"
        container={containerRef.current}
      >
        <LinearProgress
          value={stepProgress}
          variant="determinate"
          sx={{
            position: 'absolute',
            width: '100%',
            top: '100%',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        />
      </Slide>
      <AppMenuDrawer isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} />
    </Box>
  );
};
